import { useCallback } from "react"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import classNames from "classnames"
import { ClickAwayListener, Button } from "@mui/material"
import { Tonality } from "@mui/icons-material"
import OPTooltip from "components/material/tooltip"
import { BASE_TYPES } from "helpers/base"
import { getIsBenchmarkEnabled } from "selectors/features"
import BaseSearch, { SELECT_BENCHMARK_PLACEHOLDER } from "./components/base-search"
import "./style.scss"

const SelectorButton = ({ active, useDefaultActiveBenchmarkSelector = true, onToggleActive, disabled, disabledTooltip }) => {
  if (active) {
    return null
  }

  if (useDefaultActiveBenchmarkSelector) {
    return <OPTooltip title={disabled ? disabledTooltip : null}>
      <span className="base-selector-button">
        <Button
          disabled={disabled}
          onClick={onToggleActive}
          variant="text"
          startIcon={<Tonality className="base-selector-icon" />}
        >
          <span className="search-benchmark">{SELECT_BENCHMARK_PLACEHOLDER}</span>
        </Button>
      </span>
    </OPTooltip>
  }

  return (
    <div className="base-selector-button" onClick={onToggleActive}>
      <Tonality fontSize="small" />
      <span className="compare-title">Active</span>
    </div>
  )
}

SelectorButton.propTypes = {
  active: PropTypes.bool.isRequired,
  useDefaultActiveBenchmarkSelector: PropTypes.bool,
  onToggleActive: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
}

export default function BaseSelector({
  disabled,
  disabledTooltip,
  baseSearch,
  active,
  searchResults,
  searchValue,
  setSelectedBase,
  portfolioBenchmark,
  baseHistory,
  selectedBase,
  baseInputFocus,
  baseInputBlur,
  baseDropdownVisible,
  defaultSelectedBase,
  alternateLayout,
  openCompositeSecurity,
  modelSupportsComposites,
  useDefaultActiveBenchmarkSelector,
  onBaseToggle,
  modelId,
}) {
  const isBenchmarkEnabled = useSelector(getIsBenchmarkEnabled)

  const handleToggleActive = useCallback(() => {
    if (!active) {
      if (!isBenchmarkEnabled && defaultSelectedBase?.type === BASE_TYPES.benchmark) {
        baseInputFocus()
        return
      }
      onBaseToggle?.(true)
      setSelectedBase(defaultSelectedBase, modelId)
    } else {
      onBaseToggle?.(false)
      setSelectedBase(null, modelId)
    }
  }, [onBaseToggle, active, setSelectedBase, defaultSelectedBase, modelId, isBenchmarkEnabled, baseInputFocus])

  const dropdownOpenWithoutSelection = !active && baseDropdownVisible

  return <ClickAwayListener onClickAway={baseInputBlur}>
    <div className={classNames("components_base-selector", { active, "alternate-layout": alternateLayout, searching: searchValue, "dropdown-open": dropdownOpenWithoutSelection })}>
      <SelectorButton
        disabled={disabled}
        disabledTooltip={disabledTooltip}
        active={active}
        useDefaultActiveBenchmarkSelector={useDefaultActiveBenchmarkSelector}
        onToggleActive={handleToggleActive}
      />
      <BaseSearch
        modelId={modelId}
        toggleActive={handleToggleActive}
        baseInputFocus={baseInputFocus}
        baseInputBlur={baseInputBlur}
        selectedBase={selectedBase}
        baseHistory={baseHistory}
        baseDropdownVisible={baseDropdownVisible}
        portfolioBenchmark={portfolioBenchmark}
        searchValue={searchValue}
        setSelectedBase={setSelectedBase}
        searchResults={searchResults}
        baseSearch={baseSearch}
        alternateLayout={alternateLayout}
        openCompositeSecurity={openCompositeSecurity}
        modelSupportsComposites={modelSupportsComposites}
        useDefaultActiveBenchmarkSelector={useDefaultActiveBenchmarkSelector}
      />
    </div>
  </ClickAwayListener>
}

BaseSelector.propTypes = {
  baseSearch: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  searchResults: PropTypes.array.isRequired,
  searchValue: PropTypes.string.isRequired,
  setSelectedBase: PropTypes.func.isRequired,
  portfolioBenchmark: PropTypes.object.isRequired,
  baseHistory: PropTypes.array.isRequired,
  selectedBase: PropTypes.object,
  baseInputFocus: PropTypes.func.isRequired,
  baseInputBlur: PropTypes.func.isRequired,
  baseDropdownVisible: PropTypes.bool.isRequired,
  defaultSelectedBase: PropTypes.object.isRequired,
  alternateLayout: PropTypes.bool,
  openCompositeSecurity: PropTypes.func.isRequired,
  modelSupportsComposites: PropTypes.bool.isRequired,
  useDefaultActiveBenchmarkSelector: PropTypes.bool,
  onBaseToggle: PropTypes.func,
  disabled: PropTypes.bool,
  disabledTooltip: PropTypes.string,
  modelId: PropTypes.string.isRequired,
}
